import React from "react";
import { IconButton } from "@fluentui/react/lib/Button";
import { Icon } from "@fluentui/react/lib/Icon";
import "./FilterDropDown.css";

export interface FilterDropDownItem {
  key: string;
  label: string;
  value: any;
  icon?: () => React.ReactElement;
  active?: boolean;
  /** Item kind. Defaults to "option" (today's behavior: click closes the dropdown and calls `setActiveFilters`). */
  type?: "checkbox" | "option" | "separator";
  /** Only relevant for `type: "checkbox"`. When true, clicking the item does not close the dropdown. Defaults to false. */
  keepOpen?: boolean;
  /** Only relevant for `type: "checkbox"`. Whether the checkbox affordance renders as checked. */
  checked?: boolean;
  /** Optional `data-testid` applied to this item's row. */
  testId?: string;
}

/** IDS-7323: safety margin kept between the dropdown menu and the bottom of the viewport. */
const MENU_VIEWPORT_MARGIN_PX = 8;
/** IDS-7323: the menu never shrinks below this height, even when the button sits very close to the viewport edge. */
const MENU_MIN_MAX_HEIGHT_PX = 120;

export const FilterDropDown = ({
  filters,
  setActiveFilters,
  onToggleItem,
}: {
  filters: FilterDropDownItem[];
  setActiveFilters: (filters: any[]) => void;
  /** Called instead of `setActiveFilters` when a `type: "checkbox"` item is clicked. */
  onToggleItem?: (item: FilterDropDownItem) => void;
}) => {
  const [dropdownOpen, setDropdownOpen] = React.useState(false);
  // IDS-7323: vertical space available for the menu below the button. `undefined` until first
  // measured, in which case the menu keeps rendering at its natural height (today's behavior).
  const [menuMaxHeight, setMenuMaxHeight] = React.useState<number | undefined>(undefined);
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  // IDS-7323: the menu is `position: absolute` inside an ancestor that clips overflow (e.g. the
  // Outline panel host), so a tall menu can be cut off with no way to reach the remaining entries.
  // Recompute how much space is actually available below the button - on open, and on resize while
  // open - so the menu can constrain its own height and scroll internally instead of being clipped.
  React.useEffect(() => {
    if (!dropdownOpen) return;

    const recomputeMenuMaxHeight = () => {
      const buttonRect = buttonRef.current?.getBoundingClientRect();
      if (!buttonRect) return;
      const availableSpace = window.innerHeight - buttonRect.bottom - MENU_VIEWPORT_MARGIN_PX;
      setMenuMaxHeight(Math.max(availableSpace, MENU_MIN_MAX_HEIGHT_PX));
    };

    recomputeMenuMaxHeight();
    window.addEventListener("resize", recomputeMenuMaxHeight);
    return () => window.removeEventListener("resize", recomputeMenuMaxHeight);
  }, [dropdownOpen]);

  return (
    <button
      ref={buttonRef}
      className="ml-2"
      style={{ position: "relative", overflow: "visible", border: "none", background: "none", padding: 0 }}
      onBlur={(e) => {
        if (e.currentTarget.contains(e.relatedTarget)) return;
        setDropdownOpen(false);
      }}
    >
      <IconButton
        aria-label="Filters"
        iconProps={{
          imageProps: { src: "/assets/filter.svg", width: "16px", height: "16px" },
        }}
        onClick={() => setDropdownOpen(!dropdownOpen)}
      >
        Filters
      </IconButton>
      <ul
        className="dropdown-menu"
        data-testid="outline-filter-dropdown"
        tabIndex={-1}
        style={{
          display: dropdownOpen ? "block" : "none",
          position: "absolute",
          right: 0,
          fontSize: "14px",
          minWidth: "200px",
          maxWidth: "200px",
          maxHeight: menuMaxHeight !== undefined ? `${menuMaxHeight}px` : undefined,
          overflowY: "auto",
        }}
      >
        {filters.map((filter) => {
          if (filter.type === "separator") {
            // `<hr>` already carries the implicit ARIA "separator" role, so the wrapping `<li>` does not need one.
            return (
              <li key={filter.key} data-testid={filter.testId}>
                <hr style={{ margin: "4px 8px", border: "none", borderTop: "1px solid #e1e1e1" }} />
              </li>
            );
          }

          const isCheckbox = filter.type === "checkbox";

          return (
            <li
              key={filter.key}
              data-testid={filter.testId}
              style={{ backgroundColor: filter.active ? "#e7e7e7" : "transparent" }}
            >
              <button
                className={`dropdown-item d-flex align-items-center${
                  isCheckbox ? " filter-dropdown-checkbox-item" : ""
                }`}
                role={isCheckbox ? "menuitemcheckbox" : "menuitem"}
                aria-checked={isCheckbox ? !!filter.checked : undefined}
                style={{
                  width: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                onClick={() => {
                  if (isCheckbox) {
                    onToggleItem?.(filter);
                    if (!filter.keepOpen) {
                      setDropdownOpen(false);
                    }
                    return;
                  }

                  setDropdownOpen(false);
                  if (filter.value === undefined) {
                    setActiveFilters([]);
                  } else {
                    setActiveFilters(Array.isArray(filter.value) ? filter.value : [filter.value]);
                  }
                }}
              >
                {isCheckbox && (
                  <span
                    aria-hidden="true"
                    style={{
                      width: "12px",
                      height: "12px",
                      marginRight: "6px",
                      flexShrink: 0,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "1px solid #787878",
                    }}
                  >
                    {filter.checked && <Icon iconName="CheckMark" style={{ fontSize: "10px", color: "#787878" }} />}
                  </span>
                )}
                {!isCheckbox && <span style={{ width: "25px" }}>{filter.icon?.()}</span>}
                <span style={{ textOverflow: "ellipsis", overflow: "hidden", width: "100%", whiteSpace: "nowrap" }}>
                  {filter.label}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </button>
  );
};
